import { useQuery } from "urql";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import Checkbox from "@/components/ui/forms/checkbox";
import InlineSearchEditor from "@/components/ui/tiptap-editor/InlineSearchEditor";
import { GetAggregateOfAllArticles } from "@/data-access/news-feeds/queries/articles-management-queries";
import { addCommasToNumbers, pluralize } from "@/lib/utils/prettyName";
import { useArticleMetricsAsPercentages } from "@/store/news-feed/feed-content-management/feed-content-management.selectors";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { trimKeyMessageName } from "@/store/news-feed/key-messages/key-message.selectors";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import ArticleSortController from "./ArticleSortController";

interface ArticleContentControlPanelProps {
  stickyTopOffset: number;
  handleSearch: (searchValue: string) => Promise<void>;
}

const ArticleContentControlPanel = ({
  stickyTopOffset,
  handleSearch,
}: ArticleContentControlPanelProps) => {
  const { feedId, startDate, endDate } = useAnalysisParamsForQueries();
  const isInitialLoading = useFeedContentManagementStore(
    (state) => state.isInitialLoading,
  );
  const filteredArticlesCount = useFeedContentManagementStore(
    (state) => state.pagination.totalCount,
  );
  const isAllSelected = useFeedContentManagementStore(
    (state) => state.isAllSelected,
  );
  const selectedArticleIds = useFeedContentManagementStore(
    (state) => state.selectedArticleIds,
  );
  const excludedArticleIds = useFeedContentManagementStore(
    (state) => state.excludedArticleIds,
  );
  const setIsAllSelected = useFeedContentManagementStore(
    (state) => state.setIsAllSelected,
  );
  const clearSelection = useFeedContentManagementStore(
    (state) => state.clearSelection,
  );

  // Calculate indeterminate state: some articles are individually selected (but not all)
  const indeterminate =
    (!isAllSelected && selectedArticleIds.size > 0) ||
    (isAllSelected && excludedArticleIds.size > 0);
  const selectAllCheckedState = indeterminate ? "indeterminate" : isAllSelected;

  const setSaveSearchKeyMessageModalOpen = useKeyMessageStore(
    (state) => state.setSaveSearchKeyMessageModalOpen,
  );
  const setKeyMessageName = useKeyMessageStore(
    (state) => state.setKeyMessageName,
  );
  const searchTerm = useFeedContentManagementStore((state) => state.searchTerm);
  const setKeyMessageSearchTerm = useKeyMessageStore(
    (state) => state.setSearchTerm,
  );
  const setIsAdvancedSearch = useFeedContentManagementStore(
    (state) => state.setIsAdvancedSearch,
  );
  const isAdvancedSearch = useFeedContentManagementStore(
    (state) => state.isAdvancedSearch,
  );
  const isFetchingArticles = useFeedContentManagementStore(
    (state) => state.isFetchingArticles,
  );

  const queryEnabled =
    feedId !== undefined && startDate !== undefined && endDate !== undefined;

  const [aggregateResult] = useQuery({
    query: GetAggregateOfAllArticles,
    variables: {
      filters: {
        feedId,
        dateRange: {
          startDate,
          endDate,
        },
      },
    },
    pause: !queryEnabled,
  });

  const totalArticles =
    aggregateResult.data?.feedArticleProjections.totalCount ?? 0;
  const { selection: selectedPercentage, displayed: displayedPercentage } =
    useArticleMetricsAsPercentages(totalArticles);

  const handleSelectAll = () => {
    if (isAllSelected) {
      clearSelection();
    } else {
      setIsAllSelected(true);
    }
  };

  const renderNumberOfArticles = () => {
    if (aggregateResult.fetching || isInitialLoading) {
      return <Skeleton className="h-5 w-32" />;
    }

    const percentageToShow =
      selectedPercentage && selectedPercentage > 0
        ? selectedPercentage
        : displayedPercentage;

    return (
      <>
        <span className="font-semibold">
          {`${addCommasToNumbers(filteredArticlesCount ?? 0)} `}
        </span>
        <span>{pluralize(totalArticles, "Article")} </span>
        {percentageToShow !== null && percentageToShow > 0 && (
          <span>({percentageToShow}%)</span>
        )}
        {(percentageToShow === null || percentageToShow === 0) && (
          <span className="text-xs text-gray-600">
            (Adjust filters to see more)
          </span>
        )}
      </>
    );
  };

  return (
    <div
      className="sticky bg-white z-10 py-3 mb-2"
      style={{ top: stickyTopOffset > 0 ? `${stickyTopOffset}px` : "0px" }}
    >
      <div className="flex gap-3 items-center justify-between">
        <div className="flex gap-3 items-center">
          <Checkbox
            checked={selectAllCheckedState}
            isSelectAll
            isPartialSelect={indeterminate}
            onCheckedChange={handleSelectAll}
            aria-label="Select all articles"
          />
          <div className="text-sm">{renderNumberOfArticles()}</div>
          <ArticleSortController />
        </div>
        {!isAdvancedSearch && (
          <InlineSearchEditor
            content={searchTerm}
            handleSearch={handleSearch}
            handleExpand={() => {
              setIsAdvancedSearch(true);
            }}
            handleSave={(editor) => {
              setKeyMessageName(trimKeyMessageName(editor.getText()));
              setSaveSearchKeyMessageModalOpen(true);
              setKeyMessageSearchTerm(editor.getText());
            }}
            isSearching={isFetchingArticles && !!searchTerm}
          />
        )}
      </div>
    </div>
  );
};

export default ArticleContentControlPanel;
